import React from "react";
import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";
import { catchHandler, elseHandler } from "../../../helpers/dispatch";
import { alias } from "../../../helpers/utils";


interface CreateEstimationCustomerDetailsParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
  onDuplicateEmailError?: (data: any) => void;
}

export const createEstimationCustomerDetailsAPI =
  ({ payload, onSuccess, onError, onDuplicateEmailError }: CreateEstimationCustomerDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Create estimation error:", error);

      if (error?.response?.data?.type === "POPUP" && onDuplicateEmailError) {
        onDuplicateEmailError(error?.response?.data);
      } else {
        onError?.();
        const errorMessage = error?.response?.data?.message;
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

const getAppBasePath = () => {
  if (alias) {
    const cleanAlias = alias.replace(/^\/+|\/+$/g, "");
    return cleanAlias ? `/${cleanAlias}` : "";
  }
  const pathname = window.location.pathname;
  const match = pathname.match(/^(.*?)\/(estimates|quotations|quote-requests)/);
  if (match && match[1]) {
    return match[1];
  }
  return "";
};

interface UpdateEstimationParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
  onWarnings?: (warnings: any[], severity: string) => void;
  onDuplicateEmailError?: (data: any) => void;
  onLocked?: () => void;
  navigate?: (path: string, options?: any) => void;
}

export const updateEstimationAPI =
  ({
    payload,
    onSuccess,
    onError,
    onWarnings,
    onDuplicateEmailError,
    onLocked,
    navigate,
  }: UpdateEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/mezzanine-storage-type/estimation/update`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (response.status === 207) {
        console.log("Warnings", result.data?.warnings);
        onWarnings?.(result.data?.warnings || [], result.severity || result.data?.severity || "");
        return;
      }

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update estimation error:", error);

      const status = error?.response?.status;
      if (status === 417) {
        const estimationId =
          payload?.estimation_id ||
          payload?._id ||
          payload?.estimationId ||
          new URLSearchParams(window.location.search).get("estimationId");
        
        try {
          sessionStorage.setItem(
            "status_lock_redirect_toast",
            JSON.stringify({
              entity: "estimation",
              status: "Quote Created",
            }),
          );
        } catch (e) {}

        message.info({
          content: React.createElement(
            "span",
            null,
            "This estimation has already been converted to ",
            React.createElement("strong", null, "Quote Created"),
            ". So, cannot be edited.",
          ),
          key: "status_lock_redirect_toast",
        });
        const targetPath = estimationId
          ? `/estimates/${estimationId}`
          : `/estimates`;
        if (navigate) {
          navigate(targetPath, { replace: true });
        } else if (onLocked) {
          onLocked();
        } else {
          const basePath = getAppBasePath();
          window.location.href = `${basePath}${targetPath}`;
        }
        return;
      }

      if (error?.response?.data?.type === "POPUP" && onDuplicateEmailError) {
        onDuplicateEmailError(error?.response?.data);
      } else {
        onError?.();
        const errorMessage = error?.response?.data?.message;
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

interface GetEstimationDetailsParams {
  estimation_id: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getEstimationDetailsAPI =
  ({ estimation_id, onSuccess, onError }: GetEstimationDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/mezzanine-storage-type/estimation/${estimation_id}`,
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch estimation details");
        onError?.();
      }
    } catch (error: any) {
      console.error("Get estimation details error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to fetch estimation details";
      message.error(errorMessage);
    }
  };

export interface EstimationFilterParams {
  page: number;
  limit: number;
  search?: string;
  date_from?: string;
  date_to?: string;
  sortBy?: string | null;
  sortOrder?: "asc" | "desc" | null;
  status?: string;
  customer_id?: string[];
  created_by?: string[];
}

export const fetchEstimationsQueryFn = async (
  filters: EstimationFilterParams,
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.get(
    "/mezzanine-storage-type/estimation/list",
    {
      params: filters,
      signal,
    },
  );

  const result = responseDataFormate(response.data);

  if (result?.success) {
    return result.data;
  } else {
    throw new Error(result?.message || "Failed to fetch Estimations");
  }
};

interface DeleteEstimationParams {
  id: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const deleteEstimationAPI =
  ({id, onSuccess, onError }: DeleteEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/mezzanine-storage-type/estimation/remove`, 
        { data: { estimation_id: id } }
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to remove estimation");
        onError?.();
      }
    } catch (error: any) {
      console.error("Get estimation details error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to remove estimation details";
      message.error(errorMessage);
    }
  };

interface GetEstimationServicesParams {
  filters: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getEstimationServicesListAPI =
  ({
    filters,
    onSuccess,
    onError,
    signal,
  }: GetEstimationServicesParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/services/list`, {
        params: filters,
        signal: signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Estimation Services Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetEstimationDeliveryOptionsParams {
  estimation_id: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getEstimationDeliveryOptionsAPI =
  ({ estimation_id, onSuccess, onError }: GetEstimationDeliveryOptionsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/mezzanine-storage-type/estimation/${estimation_id}/delivery-options`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Estimation Delivery Options Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface CreateEstimationNoteParams {
  estimation_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createEstimationNoteAPI =
  ({ estimation_id, payload, onSuccess, onError }: CreateEstimationNoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/${estimation_id}/notes`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Estimation Note error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong");
    }
  };

interface UpdateEstimationNoteParams {
  estimation_id: string;
  note_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateEstimationNoteAPI =
  ({
    estimation_id,
    note_id,
    payload,
    onSuccess,
    onError,
  }: UpdateEstimationNoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/mezzanine-storage-type/estimation/${estimation_id}/notes/${note_id}`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Estimation Note error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong");
    }
  };

interface DeleteEstimationNoteParams {
  estimation_id: string;
  note_id: string;
  type: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteEstimationNoteAPI =
  ({
    estimation_id,
    note_id,
    type,
    onSuccess,
    onError,
  }: DeleteEstimationNoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/mezzanine-storage-type/estimation/${estimation_id}/notes/${note_id}`,
        {
          params: {
            type,
          },
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.();
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Estimation Note error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong");
    }
  };

export const calculateFloorCostAPI =
  ({ payload, onSuccess, onError }: { payload: any; onSuccess: (data: any) => void; onError: () => void }) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/calculate-floor-cost`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Calculate Floor Cost Error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong during cost calculation");
    }
  };


interface UpdateEstimationStatusParams {
  payload: {
    estimation_id: string;
    status: string;
  };
  onSuccess: () => void;
  onError: () => void;
}

export const updateEstimationStatusAPI =
  ({ payload, onSuccess, onError }: UpdateEstimationStatusParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/mezzanine-storage-type/estimation/status`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.();
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update estimation status error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Something went wrong");
    }
  };


interface GenerateEstimationPdfParams {
  payload: {
    estimation_id: string;
  };
  onSuccess?: () => void;
  onError?: () => void;
}

export const generateEstimationPdfAPI =
  ({ payload, onSuccess, onError }: GenerateEstimationPdfParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/generate-pdf`,
        payload,
        {
          responseType: "blob",
        },
      );

      let fileName = "Estimation.pdf";

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      onSuccess?.();
      message.success("Estimation PDF downloaded successfully!");
    } catch (error: any) {
      console.error("Generate estimation pdf error:", error);
      onError?.();
      message.error("Failed to download PDF");
    }
  };

interface SubmitSurveyDetailsParams {
  estimation_id: string;
  payload: {
    mezzanine_floor?: {
      square_metreage: number;
      ffl: number;
      staircase_type: string;
      pallet_gate_type: string;
      total_length_of_handrail: number;
      floor_colour: string;
      gate_colour: string;
    };
    site_survey_description?: string;
    site_survey_files?: string[];
    survey_description?: string;
    survey_docs?: string[];
    manufacture_quote_amount?: number;
    is_submit?: boolean;
    is_quote_submit?: boolean;
  };
  onSuccess?: (res: any) => void;
  onError?: (err: any) => void;
}

export const submitSurveyDetailsAPI =
  ({ estimation_id, payload, onSuccess, onError }: SubmitSurveyDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/mezzanine-storage-type/estimation/${estimation_id}/survey/submit`,
        payload
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data || result);
      } else {
        message.error(result?.message || "Failed to submit survey details");
        onError?.(result);
      }
    } catch (error: any) {
      console.error("Submit survey details error:", error);
      onError?.(error);
      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Failed to submit survey details");
    }
  };

interface GetEstimationOptionsParams {
  estimation_id?: string;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const getEstimationOptionsAPI =
  ({ estimation_id, onSuccess, onError }: GetEstimationOptionsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/mezzanine-storage-type/estimation/options`,
        {
          params: estimation_id ? { estimation_id } : {},
        },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch estimation options");
        onError?.();
      }
    } catch (error: any) {
      console.error("Get estimation options error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to fetch estimation options";
      message.error(errorMessage);
    }
  };

interface CreateQuoteFromEstimationParams {
  payload: {
    estimation_id: string;
  };
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const createQuoteFromEstimationAPI =
  ({ payload, onSuccess, onError }: CreateQuoteFromEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/create-quote`,
        payload,
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Quote created successfully");
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "estimation", result);
        onError?.(result);
      }
    } catch (error: any) {
      catchHandler(dispatch, "estimation")(error);
      onError?.(error);
    }
  };

interface MergeQuoteFromEstimationParams {
  estimation_id: string;
  quote_id: string;
  force_update?: boolean;
  onSuccess?: (data: any, fullResult?: any) => void;
  onError?: (error: any, fullResult?: any) => void;
}

export const mergeQuoteFromEstimationAPI =
  ({ estimation_id, quote_id, force_update = false, onSuccess, onError }: MergeQuoteFromEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/merge-quote`,
        {
          estimation_id,
          quote_id,
          force_update,
        },
      );
      let result = responseDataFormate(response.data);

      const hasWarning =
        result?.has_warning ||
        result?.data?.has_warning ||
        result?.warning ||
        result?.data?.warning ||
        result?.status_code === 428 ||
        result?.status_code === 420;

      if (result?.success) {
        if (!hasWarning) {
          message.success(result?.message || "Quote merged successfully");
        }
        onSuccess?.(result.data, result);
      } else {
        if (hasWarning) {
          onError?.(result, result);
        } else {
          elseHandler(dispatch, "estimation", result);
          onError?.(result, result);
        }
      }
    } catch (error: any) {
      const resData = error?.response?.data
        ? responseDataFormate(error.response.data)
        : null;

      const hasWarning =
        resData?.has_warning ||
        resData?.data?.has_warning ||
        resData?.warning ||
        resData?.data?.warning ||
        resData?.status_code === 428 ||
        resData?.status_code === 420 ||
        error?.response?.status === 428 ||
        error?.response?.status === 420;

      if (hasWarning && resData) {
        onError?.(resData, resData);
      } else {
        catchHandler(dispatch, "estimation")(error);
        onError?.(error, resData);
      }
    }
  };

interface ToggleArchiveEstimationParams {
  estimation_id: string;
  is_archived: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const toggleArchiveEstimationAPI =
  ({ estimation_id, is_archived, onSuccess, onError }: ToggleArchiveEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/mezzanine-storage-type/estimation/archive`,
        {
          estimation_id,
          is_archived,
        },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || `Estimation ${is_archived ? "archived" : "unarchived"} successfully`);
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "estimation", result);
        onError?.(result);
      }
    } catch (error: any) {
      catchHandler(dispatch, "estimation")(error);
      onError?.(error);
    }
  };

interface NotifyClientEstimationParams {
  estimation_id: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const notifyClientEstimationAPI =
  ({ estimation_id, onSuccess, onError }: NotifyClientEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/notify`,
        {
          estimation_id,
        },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Notification sent to client successfully",
        );
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "estimation", result);
        onError?.(result);
      }
    } catch (error: any) {
      catchHandler(dispatch, "estimation")(error);
      onError?.(error);
    }
  };

interface DuplicateEstimationParams {
  estimation_id: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const duplicateEstimationAPI =
  ({ estimation_id, onSuccess, onError }: DuplicateEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/duplicate`,
        {
          estimation_id,
        },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Estimation duplicated successfully");
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "estimation", result);
        onError?.(result);
      }
    } catch (error: any) {
      catchHandler(dispatch, "estimation")(error);
      onError?.(error);
    }
  };

interface GetEstimationAuditLogParams {
  filters: {
    estimation_id: string;
    page?: number;
    limit?: number;
  };
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

interface CreateRevisionEstimationParams {
  estimation_id: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

export const createRevisionEstimationAPI =
  ({ estimation_id, onSuccess, onError }: CreateRevisionEstimationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/mezzanine-storage-type/estimation/create-revision`,
        {
          estimation_id,
        },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Estimation revision created successfully");
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "estimation", result);
        onError?.(result);
      }
    } catch (error: any) {
      catchHandler(dispatch, "estimation")(error);
      onError?.(error);
    }
  };

export const getEstimationAuditLogListAPI =
  ({ filters, onSuccess, onError }: GetEstimationAuditLogParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/mezzanine-storage-type/estimation/audit-logs/${filters?.estimation_id}`,
        {
          params: {
            page: filters?.page || 1,
            limit: filters?.limit || 100,
          },
        },
      );
      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "estimation", result);
        onError?.(result);
      }
    } catch (error: any) {
      catchHandler(dispatch, "estimation")(error);
      onError?.(error);
    }
  };










